import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import { DeliveryType } from "../../../common/types/enums";
import { CartItem, PromoInterface } from "../../../common/types/interfaces";
import { getApi } from "../../../api/api";
import { selectCity } from "../../../store/slices/citySlice";
import { useCartValidation } from "./useCartValidation";

interface UsePromoManagementProps {
  cartItems: CartItem[];
  deliveryType: DeliveryType;
  pickUpStore?: number;
  clusterId: number;
  onGiftAutoSelect?: (giftItemId: number) => void;
}

export const usePromoManagement = ({
  cartItems,
  deliveryType,
  pickUpStore,
  clusterId,
  onGiftAutoSelect,
}: UsePromoManagementProps) => {
  const api = getApi();
  const selectedCity = useSelector(selectCity);

  const [selectedPromo, setSelectedPromo] = useState<PromoInterface | null>(
    null,
  );
  const [discountCode, setDiscountCode] = useState<string>("");
  const [applicablePromos, setApplicablePromos] = useState<PromoInterface[]>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const { findApplicablePromos, isPromoApplicable } = useCartValidation();

  // Загружаем промо кластера
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const result = await api.getPromos({
          cityId: selectedCity?.id,
          limit: 1000,
          offset: 0,
        });
        if (!cancelled) {
          const list = (result?.data || []).filter(
            (p) => Number(p.clusterId) === Number(clusterId),
          );
          const storeId =
            deliveryType === DeliveryType.PICKUP ? pickUpStore : undefined;
          setApplicablePromos(
            findApplicablePromos(list, cartItems, storeId, deliveryType),
          );
        }
      } catch {
        if (!cancelled) setApplicablePromos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (clusterId) load();

    return () => {
      cancelled = true;
    };
  }, [
    api,
    clusterId,
    selectedCity?.id,
    cartItems,
    deliveryType,
    pickUpStore,
    findApplicablePromos,
  ]);

  // Если выбранная промо больше не подходит — сбрасываем
  useEffect(() => {
    if (
      selectedPromo &&
      !applicablePromos.some((p) => p.id === selectedPromo.id)
    ) {
      setSelectedPromo(null);
      setDiscountCode("");
    }
  }, [applicablePromos, selectedPromo]);

  const validatePromo = useCallback((): boolean => {
    if (!selectedPromo) return true;

    const storeId =
      deliveryType === DeliveryType.PICKUP ? pickUpStore : undefined;

    return isPromoApplicable(
      selectedPromo,
      cartItems,
      storeId,
      discountCode,
      deliveryType,
    );
  }, [
    selectedPromo,
    cartItems,
    deliveryType,
    pickUpStore,
    discountCode,
    isPromoApplicable,
  ]);

  const handleSelectPromo = useCallback(
    (promo: PromoInterface | null) => {
      setSelectedPromo(promo);
      setDiscountCode("");

      if (promo?.giftBySum && promo.giftItemIds?.length === 1) {
        onGiftAutoSelect?.(promo.giftItemIds[0]);
      }
    },
    [onGiftAutoSelect],
  );

  return {
    selectedPromo,
    setSelectedPromo: handleSelectPromo,
    discountCode,
    setDiscountCode,
    applicablePromos,
    validatePromo,
    loading,
  };
};
